import { useNavigate, useParams } from "@tanstack/react-router";

import { useNutrient, useUpdateNutrient } from "@/lib/api/nutrients";
import { ApiError } from "@/lib/api/client";
import { ErrorState } from "@/components/error-state";
import { OptimisticLockBanner } from "@/components/optimistic-lock-banner";

import {
  NutrientForm,
  emptyToUndef,
  parseOptNum,
  type NutrientFormValues,
} from "./form";

export function NutrientEditPage() {
  const { nutrientId } = useParams({
    from: "/chemistry-profile/nutrients/$nutrientId/edit",
  });
  const navigate = useNavigate();
  const q = useNutrient(nutrientId);
  const update = useUpdateNutrient(nutrientId);

  if (q.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.error) return <ErrorState error={q.error} retry={q.refetch} />;
  if (!q.data) return null;
  if (q.data.system_provided) {
    return (
      <p className="text-sm text-muted-foreground">
        System nutrients are read-only; clone to a tenant copy first.
      </p>
    );
  }

  function submit(values: NutrientFormValues) {
    if (!q.data) return;
    update.mutate(
      {
        id: nutrientId,
        expected_updated_at: q.data.updated_at,
        name: values.name,
        manufacturer: emptyToUndef(values.manufacturer),
        base_dose_ml_per_gal: parseOptNum(values.base_dose_ml_per_gal),
        delta_ec_per_ml_per_gal: parseOptNum(values.delta_ec_per_ml_per_gal),
        delta_ph_per_ml_per_gal: parseOptNum(values.delta_ph_per_ml_per_gal),
        mixing_order_rank: Number(values.mixing_order_rank),
        contains_calcium: !!values.contains_calcium,
        contains_phosphate: !!values.contains_phosphate,
        is_biological: !!values.is_biological,
        is_ph_adjuster: !!values.is_ph_adjuster,
        notes: emptyToUndef(values.notes),
        reason: emptyToUndef(values.reason),
      },
      {
        onSuccess: () => navigate({ to: "/chemistry-profile/nutrients" }),
      },
    );
  }

  const stale = update.error instanceof ApiError && update.error.isStale;
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Edit nutrient</h1>
      <NutrientForm
        initial={q.data}
        submitting={update.isPending}
        showReason
        submitLabel="Save changes"
        onSubmit={submit}
        banner={
          stale ? (
            <OptimisticLockBanner
              error={update.error}
              onRefresh={() => {
                update.reset();
                void q.refetch();
              }}
            />
          ) : undefined
        }
      />
      {update.error && !stale && (
        <ErrorState error={update.error} retry={() => update.reset()} />
      )}
    </div>
  );
}
